import React from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1
          className="font-display text-2xl font-semibold tracking-tight text-[color:var(--mk-text)] sm:text-3xl"
          data-testid="page-title"
        >
          {title}
        </h1>
        {subtitle && <p className="mt-1.5 text-sm text-[color:var(--mk-text-2)]">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {Icon && (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[color:var(--mk-primary)]/12 ring-1 ring-[color:var(--mk-border-strong)]">
          <Icon className="h-6 w-6 text-[color:var(--mk-primary)]" strokeWidth={2} />
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-[color:var(--mk-text)]">{title}</h3>
      {description && <p className="mt-1.5 max-w-md text-sm text-[color:var(--mk-text-2)]">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/**
 * Premium dark KPI tile — thin brass top rule, inset hairline, data font value, hover raise.
 */
export function StatCard({ label, value, sub, icon: Icon, accent, testid, className }) {
  return (
    <Card
      className={cn(
        "group relative overflow-hidden border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-5",
        "shadow-[inset_0_1px_0_rgba(231,215,191,0.05),0_12px_30px_rgba(0,0,0,0.35)]",
        "transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-[color:var(--mk-border-strong)] hover:shadow-[inset_0_1px_0_rgba(231,215,191,0.06),0_18px_44px_rgba(0,0,0,0.5)]",
        className
      )}
      data-testid={testid}
    >
      {/* brass top rule */}
      <span className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[color:var(--mk-primary)]/70 to-transparent opacity-70" />
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-[color:var(--mk-muted)]">{label}</span>
        {Icon && (
          <span
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ background: (accent || "#B08D57") + "22" }}
          >
            <Icon className="h-4 w-4" strokeWidth={2.25} style={{ color: accent || "#B08D57" }} />
          </span>
        )}
      </div>
      <div className="mt-3 font-data text-2xl font-semibold leading-none tabular-nums text-[color:var(--mk-text)] sm:text-[28px]">
        {value}
      </div>
      {sub && <div className="mt-2 text-xs text-[color:var(--mk-text-2)]">{sub}</div>}
    </Card>
  );
}
